<template>
    <b-button 
        :id="buttonId"
        :variant="buttonVariant" 
        :size="buttonSize" 
        v-b-tooltip="{ title: tooltipText, placement: 'top', trigger: 'hover', disabled: !tooltipText }"
        style="min-width: 180px;"
        :disabled="loadingFileBundle"
        @click="downloadFileBundle"
    >
        <div
            v-if="loadingFileBundle"
            key="loadingBundle"
            class="d-flex align-items-center justify-content-center gap-2"
        >
            <b-spinner small class=""></b-spinner>
            <span style="font-size: 90%;">{{ loadingText }}</span>
        </div>
        <div 
            v-else
            key="notLoadingBundle"
            class="d-flex align-items-center justify-content-center gap-2"
        >
            <span class="fiv-sqo fiv-icon-zip"></span>
            <span style="font-size: 90%;">{{ finalButtonText }}</span>
        </div>
    </b-button>
</template>

<script>
import { errorHandling } from '../../js/custom-vue-mixins';
import { suppliosApi } from '../../js/api-service';
import invoke from 'lodash/invoke';
import { nanoid } from 'nanoid';

export default {
    name: 'FileDownloadBundleButton',
    components: {},
    mixins: [
        errorHandling,
    ],
    props: {  // MUST PROVIDE EITHER (endpoint + endpointArgs) OR (category + ids)
        endpoint: {  // if passing in a string, should be everything after suppliosApi ('rfx.get_file_bundle')
            type: [String, Function],
            required: false,  // use this OR category/ids
        },
        endpointArgs: {
            type: [String, Array],
            required: false,  // things to pass to endpoint when calling it - REQUIRED IF 'endpoint' prop is provided
        },
        // ALTERNATE OPTION - pass in a category and list of IDs -- uses the generic get_file_bundle_url endpoint (can take filename also)
        category: {
            type: String,
            required: false,
        },
        ids: {
            type: Array,
            required: false,
        },
        filename: {
            type: String,
            required: false,
        },
        /// GENERAL THINGS
        buttonSize: {
            type: String,
            default: 'md',
        },
        buttonVariant: {
            type: String,
            default: 'subtle-primary',
        },
        buttonText: {
            type: String,
            required: false,
            // default: 'Download all files (.zip)',  // now handled in computed property defaultBundleButtonText
        },
        loadingText: {
            type: String,
            default: 'Preparing...',
        },
        tooltipText: {  // NOTE: the tooltip looks like crap on firefox, and it doesn't hide correctly as soon as the button becomes disabled... so we should maybe remove tooltip...
            type: String,
            default: '',
            // default: 'Download all files as a single zip file',
        },
    },
    data() {
        return {
            errorMsg: 'Error',
            error: false,
            loadingFileBundle: false,
            localId: nanoid(),  // just to make sure we have a unique ID for this component instance
        }
    },
    computed: {
        configType() {
            if (!!this.endpoint && !!this.endpointArgs) {
                return 'endpoint';
            } else if (!!this.category && !!this.ids) {
                return 'category';
            } else {
                console.error(`Error in FileDownloadBundleButton:  Must provide either (endpoint + endpointArgs) OR (category + ids) props.`);
            }
            return '';
        },
        finalButtonText() {
            const defaultText = 'Download all files (.zip)';
            return this.buttonText || (this.configType === 'category' && this.ids ? (this.ids.length > 1 ? `Download all ${this.ids.length} files (.zip)` : defaultText) : defaultText);
        },
        buttonId() {
            return `file-download-bundle-button-${this.localId}`;
        },
    },
    mounted() {
        
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        async getBundleUrlCategory() {
            let downloadURL = '';
            try {
                const { data } = await suppliosApi.files.getFileBundleUrl({category: this.category, ids: this.ids, filename: this.filename});
                downloadURL = data.url;  // has 'message' also, but currently only used for errors
            } catch (err) {
                if (err.response?.status === 403) {  // if we got 403, 
                    this.emitPermissionDenied();  // put handler in parent for this, or just show something in the button?
                } else {
                    this.apiErrorHandler(err, 'Error while getting files.  Please contact Supplios support.');
                }
            }
            return downloadURL;
        },
        async getBundleUrlEndpoint() {
            let downloadURL = '';  // will be set to '' if we fail so caller knows
            const endpointArgs = Array.isArray(this.endpointArgs) ? this.endpointArgs : [this.endpointArgs];  // you allow passing in just a single string arg (like ID)
            console.log('endpointArgs', endpointArgs);
            try {
                // const { data } = await suppliosApi.rfx.get_file_bundle_url(this.rfxId);
                let resp;
                if (typeof this.endpoint === 'function') {
                    resp = await this.endpoint(...endpointArgs);
                } else {
                    resp = await invoke(suppliosApi, this.endpoint, ...endpointArgs);
                }
                const { data } = resp;
                downloadURL = data.url;  
            } catch (err) {
                if (err.response?.status === 403) {  // if we got 403, 
                    this.emitPermissionDenied();  // put handler in parent for this, or just show something in the button?
                } else {
                    this.apiErrorHandler(err, 'Error while getting files.  Please contact Supplios support.');
                }
                // just let '' return
            }
            return downloadURL;
        },
        emitPermissionDenied() {  // just a helper to emit the event from either of the configType methods
            this.$emit('permission-denied');
        },
        async downloadFileBundle() {
            // 2 step process -- get the download URL (which makes the bundle), then GET from that URL
            // gotta hide the tooltip first before the button is disabled:
            this.$root.$emit('bv::hide::tooltip', this.buttonId);
            // now handle the download process
            let downloadURL = '';
            this.loadingFileBundle = true;
            if (this.configType === 'category') {
                downloadURL = await this.getBundleUrlCategory();
            } else if (this.configType === 'endpoint') {
                downloadURL = await this.getBundleUrlEndpoint();
            } else {
                console.error('Error in FileDownloadBundleButton:  Must provide either (endpoint + endpointArgs) OR (category + ids) props.');
            }
            if (downloadURL) {
                // from here:  https://www.stefanjudis.com/snippets/how-trigger-file-downloads-with-javascript/
                console.log('Got file bundle download URL, will now trigger download');
                const link = document.createElement("a");
                link.style.display = "none";
                link.href = downloadURL;  // URL.createObjectURL(file);
                //link.download = file.name;

                // It needs to be added to the DOM so it can be clicked
                document.body.appendChild(link);
                link.click();

                // To make this work on Firefox we need to wait
                // a little while before removing it.
                setTimeout(() => {
                    URL.revokeObjectURL(link.href);
                    link.parentNode.removeChild(link);
                }, 100);  // reference just had 0 for time
            } else {
                console.log('Did not get file bundle downloadURL!  Cannot download');
            }
            this.loadingFileBundle = false;
        },
    }
}
</script>

<style>

</style>